'use client';

import Link from 'next/link';
import { trackCtaClick } from '@/lib/analytics';
import ScrollReveal from '@/components/ScrollReveal';

/**
 * CTA Section Button Configuration
 */
export interface CTAButton {
  text: string;
  href: string;
  className?: string;
}

/**
 * CTA Section Props
 */
export interface CTASectionProps {
  title: string;
  description: string;
  primaryButton: CTAButton;
  secondaryButton?: CTAButton;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  /** Location identifier for analytics tracking */
  ctaLocation?: 'header' | 'hero' | 'inline' | 'footer' | 'tool_cta' | 'blog_cta';
}

/**
 * Standard CTA Section Component
 *
 * Provides consistent blue gradient styling across all pages
 * Includes GA4 tracking for CTA clicks
 *
 * @example
 * ```tsx
 * <CTASection
 *   title="Need Expert IT and Security Guidance?"
 *   description="Our team of experts is ready to help protect your business."
 *   primaryButton={{ text: "Get Free Consultation", href: "/contact" }}
 *   secondaryButton={{ text: "View Services", href: "/services" }}
 *   ctaLocation="inline"
 * />
 * ```
 */
export default function CTASection({
  title,
  description,
  primaryButton,
  secondaryButton,
  onPrimaryClick,
  onSecondaryClick,
  ctaLocation = 'inline'
}: CTASectionProps) {
  const handlePrimaryClick = () => {
    trackCtaClick(primaryButton.text, ctaLocation, primaryButton.href);
    onPrimaryClick?.();
  };

  const handleSecondaryClick = () => {
    if (secondaryButton) {
      trackCtaClick(secondaryButton.text, ctaLocation, secondaryButton.href);
    }
    onSecondaryClick?.();
  };

  return (
    <section className="bg-gradient-to-br from-[#2cc3d1] to-[#2813e8] text-white py-16 px-4">
      <ScrollReveal animation="fade-up">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {title}
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href={primaryButton.href}
              className={
                primaryButton.className ||
                "inline-block bg-white text-[#2813e8] hover:bg-gray-100 px-8 py-4 rounded-md font-semibold transition-all active:scale-95 shadow-lg"
              }
              onClick={handlePrimaryClick}
            >
              {primaryButton.text}
            </Link>
            {secondaryButton && (
              <Link
                href={secondaryButton.href}
                className={
                  secondaryButton.className ||
                  "inline-block bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#2813e8] px-8 py-4 rounded-md font-semibold transition-all active:scale-95"
                }
                onClick={handleSecondaryClick}
              >
                {secondaryButton.text}
              </Link>
            )}
          </div>
        </div>
      </ScrollReveal>
    </section>
  );
}
